import type { FunctionComponent, ReactNode, SVGProps } from "react";
import {
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  MenuItemProps as AriaMenuItemProps,
  MenuProps as AriaMenuProps,
  MenuTrigger,
  Pressable,
  Separator,
  composeRenderProps,
} from "react-aria-components";
import { Check } from "lucide-react";
import { cn } from "@/utils/helpers";
import { KitPopover, type KitPopoverProps } from "./Popover";

/*
 * Menu (CreativeOS Library.lib.pen, Components / Overlays, style B). Floating
 * chrome (material tier 1) at radius 12, opening from the control it belongs
 * to. Rows are text, 36px at radius 10, muted until hovered; the chosen row
 * carries the accent tint and a check. No hairlines between options. Up to
 * eight rows; a longer list is a sheet or a dialog.
 */

/**
 * Open a menu from any kit control: KitMenuTrigger, then the trigger wrapped in
 * KitPressable (a KitButton or KitIconButton is a plain button, so react-aria
 * needs the wrapper to hand it the press), then KitMenuPopover with KitMenu.
 */
export { MenuTrigger as KitMenuTrigger, Pressable as KitPressable };

/** The floating panel a menu opens in. */
export function KitMenuPopover({ className, ...props }: KitPopoverProps) {
  return <KitPopover placement="bottom end" {...props} className={cn("min-w-[200px]", className as string)} />;
}

export function KitMenu<T extends object>({ className, ...props }: AriaMenuProps<T>) {
  return (
    <AriaMenu
      {...props}
      className={cn("max-h-[inherit] overflow-auto p-1.5 outline-none", className as string)}
    />
  );
}

export interface KitMenuItemProps extends Omit<AriaMenuItemProps, "children"> {
  children: ReactNode;
  icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  /** Destructive rows (Delete post) take the alert ink. */
  danger?: boolean;
}

export function KitMenuItem({ children, icon: Glyph, danger, className, ...props }: KitMenuItemProps) {
  const textValue = props.textValue ?? (typeof children === "string" ? children : undefined);
  return (
    <AriaMenuItem
      {...props}
      textValue={textValue}
      className={composeRenderProps(className, (cls, { isFocused, isSelected, isDisabled }) =>
        cn(
          "flex min-h-9 cursor-default select-none items-center gap-2.5 rounded-row px-2.5 py-2 text-[13px] font-medium outline-none",
          danger ? "text-co-alert-ink" : isFocused || isSelected ? "text-co-text" : "text-co-muted",
          isFocused && "bg-co-hover",
          isSelected && "bg-co-accent-dim",
          isDisabled && "opacity-50",
          cls,
        ),
      )}
    >
      {composeRenderProps(children, (kids, { isSelected, selectionMode }) => (
        <>
          {Glyph ? <Glyph aria-hidden className="size-3.5 shrink-0" /> : null}
          <span className="min-w-0 flex-1 truncate">{kids}</span>
          {isSelected && selectionMode !== "none" ? <Check aria-hidden className="size-3 shrink-0 text-co-accent" /> : null}
        </>
      ))}
    </AriaMenuItem>
  );
}

/** A quiet gap between groups of rows; menus draw no hairlines. */
export function KitMenuGap() {
  return <Separator className="my-1 h-px border-0 bg-transparent" />;
}
